import { useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';
import type { ReceiptBatchListParams } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptBatchJobState } from '@pflegenavi/shared/api';
import type { UrlFilters } from '@pflegenavi/web-components';
import {
  urlDateRange,
  urlString,
  useUrlFilters,
} from '@pflegenavi/web-components';

export enum ReceiptBatchStatus {
  Pending = 'Pending',
  Submitted = 'Submitted',
}

export type ReceiptBatchTab = ReceiptBatchStatus | 'all';

// Filters live in the URL so returning from a batch restores the view.
const CODECS = {
  serviceProvider: urlString('serviceProvider'),
  receiptType: urlString('receiptType'),
  status: urlString('status'),
  receiptDate: urlDateRange('receiptFrom', 'receiptTo'),
  submissionDate: urlDateRange('submittedFrom', 'submittedTo'),
};

export type ReceiptBatchFilterKey = keyof typeof CODECS;

type ReceiptBatchFilters = Pick<
  ReceiptBatchListParams['params'],
  | 'service_provider_name'
  | 'receipt_type_ids'
  | 'status'
  | 'receipt_date_range'
  | 'submission_date_range'
>;

export interface ReceiptBatchFiltersState extends UrlFilters<typeof CODECS> {
  tab: ReceiptBatchTab;
  /** The URL values in the shape the receipt batch list endpoint expects. */
  filters: ReceiptBatchFilters;
}

const toTab = (value: string | undefined): ReceiptBatchTab =>
  value === ReceiptBatchStatus.Pending || value === ReceiptBatchStatus.Submitted
    ? value
    : 'all';

const toStatus = (tab: ReceiptBatchTab): ReceiptBatchFilters['status'] => {
  if (tab === 'all') {
    return undefined;
  }
  // A pending batch has no job yet.
  if (tab === ReceiptBatchStatus.Pending) {
    return null;
  }
  return [
    ReceiptBatchJobState.Failed,
    ReceiptBatchJobState.Pending,
    ReceiptBatchJobState.Partial,
    ReceiptBatchJobState.Successful,
  ];
};

// Both dates are datetimes. The upper bound is exclusive, the start of the
// next day, so the last second of the day is not cut off.
const toRange = (
  range: { from?: Date; to?: Date } | undefined
): [Date | undefined, Date | undefined] | undefined =>
  range
    ? [range.from, range.to ? startOfDay(addDays(range.to, 1)) : undefined]
    : undefined;

export function useReceiptBatchFilters(): ReceiptBatchFiltersState {
  const urlFilters = useUrlFilters(CODECS);
  const { values } = urlFilters;
  const tab = toTab(values.status);

  const filters = useMemo<ReceiptBatchFilters>(
    () => ({
      service_provider_name: values.serviceProvider,
      receipt_type_ids: values.receiptType ? [values.receiptType] : undefined,
      status: toStatus(tab),
      receipt_date_range: toRange(values.receiptDate),
      submission_date_range: toRange(values.submissionDate),
    }),
    [values, tab]
  );

  return useMemo(
    () => ({ ...urlFilters, tab, filters }),
    [urlFilters, tab, filters]
  );
}
